import { DecimalSchema } from "../../decimal";
import { z } from "zod";

const HubUserSchema = z.object({
  id: z.number(),
  defiAddress: z.string(),
  createdAt: z.coerce.date(),
  isBanned: z.boolean(),
  isAmbassador: z.boolean().optional().default(false),
  /** User-set Arena nickname. Surfaced on PnL competition leaderboards in place of the address. */
  nickname: z.string().nullable().optional(),
});

const LeaderboardRecordSchema = z.object({
  id: z.number(),
  leaderboardId: z.number(),
  hubUserId: z.number(),
  rank: z.number(),
  volumeUSD: DecimalSchema,
  tradedVolumeUSD: DecimalSchema,
  rewardTokenAmount: DecimalSchema,
  rewardTokenSymbol: z.string(),
  rewardTokenAddress: z.string(),
  rewardClaimed: z.coerce.date().nullable(),
  score: DecimalSchema.nullable().optional(),
  tradesCount: z.number(),
  hubUser: HubUserSchema,
  /**
   * Set by the trade-reporting external-order detection flow when a user
   * is found to have traded the campaign pair outside Cede during the
   * audit window. `null` for the vast majority of records. Optional in
   * the schema so older API responses (pre-DEV-546) still parse.
   */
  disqualifiedReason: z.string().nullable().optional(),
});

/**
 * Served while an Arena epoch is live: no volume or score, whose step at each fill is that fill's notional. The
 * min-volume gate is read for the player instead; the viewer's own volume comes from `/me`.
 */
const RedactedLeaderboardRecordSchema = LeaderboardRecordSchema.omit({
  volumeUSD: true,
  tradedVolumeUSD: true,
  score: true,
}).extend({
  isBelowMinVolume: z.boolean(),
});

const TradeToEarnLeaderboardBaseSchema = z.object({
  id: z.number(),
  campaign: z.string(),
  epochId: z.number(),
  rewardVaultAddress: z.string(),
  cooldownEnd: z.coerce.date().nullable(),
});

export const TradeToEarnLeaderboardSchema = TradeToEarnLeaderboardBaseSchema.extend({
  leaderboardRecords: LeaderboardRecordSchema.array(),
});

/** `GET v1/hub/leaderboard` and the room stream's LEADERBOARD frame. */
export const TradeToEarnLeaderboardWireSchema = TradeToEarnLeaderboardBaseSchema.extend({
  leaderboardRecords: z.union([LeaderboardRecordSchema, RedactedLeaderboardRecordSchema]).array(),
});

export type TradeToEarnLeaderboardDto = z.infer<typeof TradeToEarnLeaderboardSchema>;
export type LeaderboardRecordDto = z.infer<typeof LeaderboardRecordSchema>;
export type RedactedLeaderboardRecordDto = z.infer<typeof RedactedLeaderboardRecordSchema>;
export type TradeToEarnLeaderboardWireDto = z.infer<typeof TradeToEarnLeaderboardWireSchema>;
export type LeaderboardWireRecordDto = TradeToEarnLeaderboardWireDto["leaderboardRecords"][number];

export const isRedactedLeaderboardRecord = (record: LeaderboardWireRecordDto): record is RedactedLeaderboardRecordDto =>
  "isBelowMinVolume" in record;

/** Narrows a board that is never redacted (Hub campaigns, a settled Arena epoch). Throws rather than guess. */
export const requireFullLeaderboard = (leaderboard: TradeToEarnLeaderboardWireDto): TradeToEarnLeaderboardDto => ({
  ...leaderboard,
  leaderboardRecords: leaderboard.leaderboardRecords.map((record) => {
    if (isRedactedLeaderboardRecord(record)) {
      throw new Error(`Unexpected redacted leaderboard record for hubUserId ${record.hubUserId}`);
    }
    return record;
  }),
});

export const EpochsByCampaignSchema = z.record(z.string(), z.array(z.number()));
export type EpochsByCampaignDto = z.infer<typeof EpochsByCampaignSchema>;

export const MyLeaderboardEpochsSchema = z.object({
  epochIds: z.array(z.number().int()),
});
export type MyLeaderboardEpochsDto = z.infer<typeof MyLeaderboardEpochsSchema>;
